import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State | null {
    const name = error.name || '';
    const msg = error.message || '';
    if (name === 'NotFoundError' || msg.includes('insertBefore') || msg.includes('removeChild')) {
      return null;
    }
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const name = error.name || '';
    const msg = error.message || '';
    if (name === 'NotFoundError' || name === 'InvalidStateError' ||
        msg.includes('insertBefore') || msg.includes('removeChild')) {
      return;
    }
    if (error.message?.includes('AbortError') || error.message?.includes('Transition was skipped')) {
      return;
    }

    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    try {
      const payload = JSON.stringify({
        message: error.message,
        stack: error.stack?.slice(0, 2000),
        componentStack: errorInfo.componentStack?.slice(0, 2000),
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      });
      navigator.sendBeacon?.('/wp-json/boat-trader-v3/v1/log-error', payload);
    } catch {}
  }

  handleCopy = () => {
    const { error, errorInfo } = this.state;
    const text = [
      `Error: ${error?.message}`,
      `Stack: ${error?.stack}`,
      `Component: ${errorInfo?.componentStack}`,
    ].join('\n\n');
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100 p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Oops! Qualcosa è andato storto</h1>
            <p className="text-gray-600 mb-4 text-sm">
              Si è verificato un errore imprevisto.
            </p>

            <details className="mb-6 text-left">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Dettagli errore</summary>
              <pre className="mt-2 p-3 bg-gray-100 rounded-lg text-xs text-left overflow-auto max-h-40 leading-tight">
                {this.state.error?.message}
                {'\n'}
                {this.state.errorInfo?.componentStack?.slice(0, 1000)}
              </pre>
              <button
                onClick={this.handleCopy}
                className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Copia errore
              </button>
            </details>

            <button
              onClick={() => { location.reload(); }}
              className="w-full py-3 px-6 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
            >
              Ricarica la pagina
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
